import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, UtensilsCrossed, ChefHat, Cake, Wine } from 'lucide-react';
import { restaurantInfo, galleryImages, restaurantDetails, siteImages } from '../data/mock';

const iconMap = {
  UtensilsCrossed,
  ChefHat,
  Cake,
  Wine,
};

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.15, ...options });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, isInView];
};

const Home = () => {
  const [welcomeRef, welcomeInView] = useInView();
  const [featureRef, featureInView] = useInView();
  const [terraceRef, terraceInView] = useInView();
  const [offerRef, offerInView] = useInView();
  const [galleryRef, galleryInView] = useInView();

  return (
    <div className="bg-[#0f0704]">
      {/* Hero Section */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 hero-parallax"
          style={{
            backgroundImage: `url('${siteImages.homeHero}')`,
          }}
        />
        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0f0704]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a0a0a]/40 to-transparent" />

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="animate-fade-in-up">
            <img
              src="https://customer-assets.emergentagent.com/job_redbull-redesign/artifacts/ltmi545z_Gemini_Generated_Image_593rn7593rn7593r.png"
              alt="La Bodega Logo"
              className="w-44 md:w-56 mx-auto mb-6 rounded-2xl shadow-2xl shadow-black/40"
            />
          </div>
          <div className="animate-fade-in-up animation-delay-200">
            <span className="inline-block text-amber-400 text-sm uppercase tracking-[0.35em] font-medium mb-4">
              Café · Restaurant · Tapas Bar
            </span>
          </div>
          <p className="animate-fade-in-up animation-delay-400 text-white/70 text-lg md:text-xl max-w-2xl mx-auto mb-4 leading-relaxed">
            {restaurantInfo.heroSubtext}
          </p>
          <p className="animate-fade-in-up animation-delay-400 font-playfair italic text-amber-400/80 text-base mb-10">
            Jeder Bissen ein Geschmackserlebnis
          </p>
          <div className="animate-fade-in-up animation-delay-600 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/speisekarte"
              className="group inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-8 py-3.5 rounded-full text-sm font-semibold tracking-wide transition-all duration-300 hover:shadow-lg hover:shadow-amber-600/20"
            >
              Speisekarte
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 border border-white/30 hover:border-white/60 text-white px-8 py-3.5 rounded-full text-sm font-semibold tracking-wide transition-all duration-300 hover:bg-white/5"
            >
              Kontakt
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1 h-2.5 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section
        ref={welcomeRef}
        className="relative py-24 md:py-32 bg-[#0f0704]"
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
            welcomeInView ? 'opacity-100' : 'opacity-0'
          }`}>
            {/* Text Content */}
            <div className={welcomeInView ? 'animate-slide-left' : ''}>
              <span className="text-amber-400 text-sm uppercase tracking-[0.3em] font-medium">
                Willkommen
              </span>
              <h2 className="font-brand text-3xl md:text-4xl lg:text-5xl text-white mt-4 mb-8 leading-tight">
                {restaurantInfo.heroText}
              </h2>
              <div className="space-y-5 text-white/60 leading-relaxed">
                <p>{restaurantInfo.welcomeText}</p>
                <p>{restaurantInfo.directionText}</p>
              </div>
              <div className="mt-8">
                <Link
                  to="/restaurant"
                  className="group inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm font-semibold tracking-wide transition-colors duration-200"
                >
                  Mehr über uns
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>

            {/* Image */}
            <div className={`${welcomeInView ? 'animate-slide-right' : ''}`}>
              <div className="relative">
                <div className="img-hover-zoom rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={siteImages.homeWelcome}
                    alt="Tapas Auswahl"
                    className="w-full h-[400px] lg:h-[500px] object-cover"
                  />
                </div>
                {/* Decorative element */}
                <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-amber-400/30 rounded-2xl -z-0" />
                <div className="absolute -top-4 -left-4 w-20 h-20 border-2 border-amber-400/20 rounded-2xl -z-0" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section
        ref={featureRef}
        className="py-24 bg-[#130905]"
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className={`text-center mb-16 ${featureInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <span className="text-amber-400 text-sm uppercase tracking-[0.3em] font-medium">
              Was uns ausmacht
            </span>
            <h2 className="font-brand text-3xl md:text-4xl text-white mt-4">
              Unsere Spezialitäten
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {restaurantDetails.features.map((feature, index) => {
              const Icon = iconMap[feature.icon];
              return (
                <div
                  key={feature.title}
                  className={`group bg-white/[0.03] border border-white/[0.06] rounded-2xl p-8 text-center hover:bg-white/[0.06] hover:border-amber-400/20 transition-all duration-500 ${
                    featureInView ? 'animate-fade-in-up' : 'opacity-0'
                  }`}
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className="w-14 h-14 mx-auto mb-5 rounded-xl bg-amber-400/10 flex items-center justify-center group-hover:bg-amber-400/20 transition-colors duration-300">
                    {Icon && <Icon size={26} className="text-amber-400" />}
                  </div>
                  <h3 className="font-playfair text-lg font-semibold text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-white/50 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Terrace Section */}
      <section
        ref={terraceRef}
        className="py-24 md:py-32 bg-[#0f0704]"
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
            terraceInView ? 'opacity-100' : 'opacity-0'
          }`}>
            {/* Image */}
            <div className={`order-2 lg:order-1 ${terraceInView ? 'animate-slide-left' : ''}`}>
              <div className="relative">
                <div className="img-hover-zoom rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={siteImages.homeTerrace}
                    alt="Unsere Terrasse"
                    className="w-full h-[400px] lg:h-[500px] object-cover"
                  />
                </div>
                <div className="absolute -bottom-4 -left-4 w-24 h-24 border-2 border-amber-400/30 rounded-2xl -z-0" />
              </div>
            </div>

            {/* Text */}
            <div className={`order-1 lg:order-2 ${terraceInView ? 'animate-slide-right' : ''}`}>
              <span className="text-amber-400 text-sm uppercase tracking-[0.3em] font-medium">
                Am Abend
              </span>
              <h2 className="font-brand text-3xl md:text-4xl lg:text-5xl text-white mt-4 mb-8 leading-tight">
                Unsere Terrasse
              </h2>
              <div className="space-y-5 text-white/60 leading-relaxed">
                <p>{restaurantInfo.terraceText}</p>
                <p>{restaurantInfo.cocktailText}</p>
                <p className="font-playfair italic text-amber-400/80">
                  {restaurantInfo.flairText}
                </p>
              </div>
              <div className="mt-8">
                <Link
                  to="/speisekarte"
                  className="group inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm font-semibold tracking-wide transition-colors duration-200"
                >
                  Speisen & Getränke entdecken
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Special Offer Section */}
      <section
        ref={offerRef}
        className="py-24 md:py-32 bg-[#0f0704] relative overflow-hidden"
      >
        {/* Subtle background accent */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-amber-500/[0.03] rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className={`${offerInView ? 'opacity-100' : 'opacity-0'}`}>
            {/* Header */}
            <div className={`text-center mb-14 ${offerInView ? 'animate-fade-in-up' : ''}`}>
              <span className="inline-block bg-amber-500/15 text-amber-400 text-xs uppercase tracking-[0.35em] font-semibold px-5 py-2 rounded-full mb-5 border border-amber-400/20">
                Tapas Angebot
              </span>
              <h2 className="font-brand text-3xl md:text-5xl text-white leading-tight">
                Ein kulinarisches Erlebnis für Zwei
              </h2>
              <p className="text-white/50 mt-4 text-lg max-w-2xl mx-auto">
                Genießen Sie einen unvergesslichen Abend zu zweit mit unserer handverlesenen Tapas-Auswahl und einem erlesenen spanischen Wein.
              </p>
            </div>

            {/* Offer Card */}
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-0 max-w-4xl mx-auto rounded-3xl overflow-hidden border border-amber-400/20 shadow-2xl shadow-amber-900/10 ${offerInView ? 'animate-scale-in' : ''}`} style={{ animationDelay: '200ms' }}>
              {/* Image */}
              <div className="img-hover-zoom h-[350px] lg:h-auto">
                <img
                  src="https://customer-assets.emergentagent.com/job_redbull-redesign/artifacts/fsbvwj9c_Screenshot%202026-04-13%20at%2023.22.40.png"
                  alt="Tapas Angebot"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Details */}
              <div className="bg-gradient-to-br from-[#1a0e08] to-[#0f0704] p-10 lg:p-12 flex flex-col justify-center">
                <h3 className="font-brand text-2xl md:text-3xl text-white mb-6">
                  Tapas für Zwei
                </h3>

                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <span className="w-7 h-7 rounded-full bg-amber-400/15 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-amber-400 text-sm font-bold">6</span>
                    </span>
                    <span className="text-white/80 text-base">Verschiedene Tapas nach Wahl des Küchenchefs</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-7 h-7 rounded-full bg-amber-400/15 flex items-center justify-center shrink-0 mt-0.5">
                      <Wine size={14} className="text-amber-400" />
                    </span>
                    <span className="text-white/80 text-base">Eine Flasche spanischer Wein</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-7 h-7 rounded-full bg-amber-400/15 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-amber-400 text-sm font-bold">~</span>
                    </span>
                    <span className="text-white/80 text-base">Eine Flasche Wasser <span className="text-white/50">(Medium oder Stilles)</span></span>
                  </li>
                </ul>

                <div className="flex items-end gap-3 mb-8">
                  <span className="font-brand text-5xl text-amber-400">55,00€</span>
                  <span className="text-white/40 text-sm pb-2">für 2 Personen</span>
                </div>

                <Link
                  to="/kontakt"
                  className="group inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-8 py-3.5 rounded-full text-sm font-semibold tracking-wide transition-all duration-300 hover:shadow-lg hover:shadow-amber-600/20 w-fit"
                >
                  Jetzt reservieren
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 overflow-hidden">
        <div
          className="absolute inset-0 hero-parallax"
          style={{
            backgroundImage: `url('${siteImages.homeCta}')`,
          }}
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-brand text-3xl md:text-5xl text-white mb-6">
            Besuchen Sie uns auf Borkum
          </h2>
          <p className="text-white/60 text-lg mb-10 leading-relaxed">
            Wir freuen uns, Sie in unserem gemütlichen Restaurant begrüßen zu dürfen.
            Bismarckstraße 16, direkt im Herzen von Borkum.
          </p>
          <Link
            to="/kontakt"
            className="group inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-10 py-4 rounded-full text-sm font-semibold tracking-wide transition-all duration-300 hover:shadow-lg hover:shadow-amber-600/20"
          >
            Jetzt Kontakt aufnehmen
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
